// Generic fetch-client core shared by every Region 13 SPA: JSON in/out, a
// typed ApiError, and a one-time 302/401 hard-redirect hook. Domain-specific
// error copy is injected via `mapError` rather than baked in here.

export class ApiError extends Error {
  code?: string
  /** The parsed JSON error body, when the response had one.
   *
   *  ⚠️ CARRIED BECAUSE `code` ALONE CANNOT SAY HOW MUCH IS WRONG. A refusal
   *  that names a number — "this game has four photos, discard two" — needs the
   *  four, and encoding it into the code string would make an open-ended
   *  enumeration of every value. The body was already parsed here to find
   *  `error`; it used to be discarded on the next line.
   *
   *  Untyped on purpose: it is whatever that endpoint sent, and every reader
   *  must narrow it themselves rather than trust a shape this file cannot
   *  know. */
  body?: unknown
  constructor(public status: number, message: string, code?: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.body = body
  }
}

export interface ApiClientOptions {
  /** Prefix prepended to every path, e.g. '/api' or '/api/admin'. */
  base: string
  /** Called on the first 302/401 so the caller can bounce to sign-in, AT MOST
   *  ONCE PER TAB rather than once per page load — see `AUTH_ATTEMPT_KEY`. */
  onAuthRedirect?: () => void
  /** Map a server error `code` (+ raw message) to a friendlier message.
   *  Return undefined to fall back to the raw message. */
  mapError?: (code: string, rawMessage: string | undefined) => string | undefined
}

/** ⚠️ WHY THIS IS SESSION STORAGE AND NOT A MODULE FLAG. Every caller's
 *  `onAuthRedirect` performs a full-page navigation (`window.location.reload()`
 *  in the console and scheduler SPAs), which tears down the module — so a
 *  module-level "already fired" flag is reset by the very act it guards. That
 *  made a 401 a RELOAD LOOP whenever reloading could not fix it: 401 → reload →
 *  fresh module → 401 → reload, forever, with the page too busy to debug in.
 *
 *  Observed on `admin.staging.ayso13.org` (2026-07-31), which was serving the SPA
 *  with no Cloudflare Access application in front of it. There was no Access
 *  cookie for a reload to acquire, so the loop could never terminate.
 *
 *  Session storage survives the navigation, so the reload is attempted exactly
 *  once per tab.
 *
 *  ⚠️ WHY THIS IS A TIMESTAMP AND NOT A BOOLEAN CLEARED ON THE NEXT `ok`
 *  RESPONSE. A single SPA page issues several requests against several
 *  endpoints, and one endpoint 401ing while a DIFFERENT endpoint succeeds does
 *  not mean auth is healthy — it means that one endpoint's failure is
 *  endpoint-specific, and a reload will not fix it. The console's
 *  `/communications/*` proxy hit exactly this: `RequireCapability` always gets
 *  a 200 from `GET /api/me` before the panel's own query runs, so an
 *  ok-clears-the-guard rule re-armed the guard on every single page load, and
 *  the very next `/api/communications/status` 401 fired the reload again —
 *  200 → reload-armed → 401 → reload → 200 → reload-armed → 401 → reload,
 *  forever. A timestamp fixes this without disabling the redirect entirely:
 *  record WHEN a reload was last attempted, and only suppress a new one within
 *  a short window. A loop retries within about a second, so it is bounded to a
 *  single reload; a genuinely expired Access cookie found hours later is well
 *  outside the window and still gets its reload. */
const AUTH_ATTEMPT_KEY = 'region13:authRedirectAttemptedAt'
const AUTH_ATTEMPT_WINDOW_MS = 30_000

/** Session storage is absent in SSR and in some test environments, and a
 *  throwing access must never break an API call. Falling back to "no attempt
 *  recorded" keeps the previous behavior rather than suppressing a needed
 *  redirect. */
function authRedirectAttemptedRecently(): boolean {
  try {
    const raw = sessionStorage.getItem(AUTH_ATTEMPT_KEY)
    if (!raw) return false
    const attemptedAt = Number(raw)
    if (!Number.isFinite(attemptedAt)) return false
    return Date.now() - attemptedAt < AUTH_ATTEMPT_WINDOW_MS
  } catch {
    return false
  }
}

function recordAuthRedirectAttempt(): void {
  try {
    sessionStorage.setItem(AUTH_ATTEMPT_KEY, String(Date.now()))
  } catch {
    // Ignore: the module flag below still bounds this page load.
  }
}

export function createApiClient(opts: ApiClientOptions) {
  let authRedirectFired = false

  // Turn a fetch Response into the parsed JSON body, or throw a typed ApiError.
  // Shared by the JSON senders and the raw-body sender below.
  async function handle<T>(r: Response): Promise<T> {
    if (r.status === 302 || r.status === 401) {
      // Two guards, deliberately: the module flag stops several in-flight calls
      // each triggering a redirect within ONE page load, and the session key
      // stops the reload repeating across page loads (or across endpoints —
      // see the timestamp comment above `AUTH_ATTEMPT_KEY`).
      if (!authRedirectFired && !authRedirectAttemptedRecently()) {
        authRedirectFired = true
        recordAuthRedirectAttempt()
        opts.onAuthRedirect?.()
        throw new ApiError(r.status, 'Unauthorized: redirecting to sign in')
      }
      // Already tried recently and still unauthorized, so reloading will not
      // help. Surface it as an ordinary error and let the app render a
      // sign-in / no-access state instead of thrashing.
      throw new ApiError(
        r.status,
        'You are not signed in, or your access has not been granted. Reloading will not help.',
      )
    }
    // No re-arm on `ok` here — deliberately. See the timestamp comment above
    // `AUTH_ATTEMPT_KEY`: an unrelated endpoint succeeding does not mean THIS
    // endpoint's 401 would be fixed by a reload, and re-arming on every `ok`
    // response is exactly what turned one 401 into an infinite loop. Re-arming
    // is handled purely by the attempt timestamp aging out.
    if (!r.ok) {
      const text = await r.text().catch(() => '')
      let message = text || `${r.status} ${r.statusText}`
      let code: string | undefined
      let body: unknown
      try {
        const parsed = JSON.parse(text) as unknown
        body = parsed
        if (parsed && typeof parsed === 'object' && 'error' in parsed && typeof (parsed as Record<string, unknown>).error === 'string') {
          code = (parsed as Record<string, unknown>).error as string
          const rawMessage = (parsed as Record<string, unknown>).message
          const rawMsg = typeof rawMessage === 'string' && rawMessage ? rawMessage : undefined
          message = opts.mapError?.(code, rawMsg) ?? rawMsg ?? code
        }
      } catch {
        // not JSON — keep raw text as message
      }
      throw new ApiError(r.status, message, code, body)
    }
    return (await r.json()) as T
  }

  async function request<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
    const r = await fetch(`${opts.base}${path}`, {
      method,
      credentials: 'same-origin',
      headers:
        body !== undefined
          ? { accept: 'application/json', 'content-type': 'application/json' }
          : { accept: 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    return handle<T>(r)
  }

  // Send a raw body (Blob/ArrayBuffer/etc.) VERBATIM — no JSON.stringify. For
  // binary endpoints (e.g. a PNG upload) where JSON-wrapping would corrupt the
  // bytes. `contentType` defaults to application/octet-stream.
  async function requestRaw<T>(method: 'PUT' | 'POST', path: string, body: BodyInit, contentType = 'application/octet-stream'): Promise<T> {
    const r = await fetch(`${opts.base}${path}`, {
      method,
      credentials: 'same-origin',
      headers: { accept: 'application/json', 'content-type': contentType },
      body,
    })
    return handle<T>(r)
  }

  return {
    get: <T,>(path: string) => request<T>('GET', path),
    post: <T,>(path: string, body: unknown) => request<T>('POST', path, body),
    put: <T,>(path: string, body: unknown) => request<T>('PUT', path, body),
    patch: <T,>(path: string, body: unknown) => request<T>('PATCH', path, body),
    del: <T,>(path: string, body?: unknown) => request<T>('DELETE', path, body),
    /** PUT a raw body with no JSON encoding (binary uploads). */
    putRaw: <T,>(path: string, body: BodyInit, contentType?: string) => requestRaw<T>('PUT', path, body, contentType),
  }
}
